import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  ClipboardCheck,
  Calendar,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  Scale,
  Battery,
  Moon,
  Smile,
  Flame,
  Brain,
  Target,
  Trophy,
  MessageSquare,
  Settings
} from 'lucide-react';

const RATING_LABELS = {
  1: 'Very Low',
  2: 'Low', 
  3: 'Below Average',
  4: 'Slightly Low',
  5: 'Average',
  6: 'Slightly High',
  7: 'Above Average',
  8: 'High',
  9: 'Very High',
  10: 'Excellent'
};

export default function CheckInsPage() {
  const { token } = useAuth();
  const [checkins, setCheckins] = useState([]);
  const [todaysCheckin, setTodaysCheckin] = useState(null);
  const [pendingCheckins, setPendingCheckins] = useState([]);
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCheckin, setActiveCheckin] = useState(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  
  // Form state for completing check-in
  const [formData, setFormData] = useState({
    weight_kg: '',
    body_fat_percentage: '',
    energy_level: 5,
    mood: 5,
    sleep_quality: 5,
    soreness_level: 5,
    stress_level: 5,
    goals_progress: '',
    challenges: '',
    wins: '',
    client_notes: ''
  });
  
  // Schedule form
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'daily',
    preferred_time: '09:00',
    reminder_enabled: true
  });

  const fetchData = useCallback(async () => {
    try {
      const [checkinsRes, todayRes, pendingRes, statsRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/checkins?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/checkins/today`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/checkins/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/checkins/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/checkins/schedule`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCheckins(checkinsRes.data);
      setTodaysCheckin(todayRes.data.has_checkin ? todayRes.data.checkin : null);
      setPendingCheckins(pendingRes.data);
      setStats(statsRes.data);
      setSchedule(scheduleRes.data);
      
      if (scheduleRes.data.frequency) {
        setScheduleForm({
          frequency: scheduleRes.data.frequency,
          preferred_time: scheduleRes.data.preferred_time || '09:00',
          reminder_enabled: scheduleRes.data.reminder_enabled ?? true
        });
      }
    } catch (error) {
      console.error('Error fetching check-ins:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCompleteCheckin = async () => {
    if (!activeCheckin) return;
    
    try {
      const params = new URLSearchParams();
      if (formData.weight_kg) params.append('weight_kg', formData.weight_kg);
      if (formData.body_fat_percentage) params.append('body_fat_percentage', formData.body_fat_percentage);
      params.append('energy_level', formData.energy_level);
      params.append('mood', formData.mood);
      params.append('sleep_quality', formData.sleep_quality);
      params.append('soreness_level', formData.soreness_level);
      params.append('stress_level', formData.stress_level);
      if (formData.goals_progress) params.append('goals_progress', formData.goals_progress);
      if (formData.challenges) params.append('challenges', formData.challenges);
      if (formData.wins) params.append('wins', formData.wins);
      if (formData.client_notes) params.append('client_notes', formData.client_notes);
      
      await axios.post(
        `${API}/checkins/${activeCheckin.id}/complete?${params.toString()}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Check-in completed! +10 points');
      setActiveCheckin(null);
      setFormData({
        weight_kg: '',
        body_fat_percentage: '',
        energy_level: 5,
        mood: 5,
        sleep_quality: 5,
        soreness_level: 5,
        stress_level: 5,
        goals_progress: '',
        challenges: '',
        wins: '',
        client_notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to complete check-in');
    }
  };

  const handleSaveSchedule = async () => {
    try {
      await axios.post(
        `${API}/checkins/schedule`,
        null,
        {
          params: scheduleForm,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Check-in schedule saved!');
      setShowScheduleDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save schedule');
    }
  };

  const RatingSlider = ({ label, icon: Icon, value, onChange, inverted = false }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-gray-300 flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {label}
        </Label>
        <span className={`text-sm font-mono ${
          inverted 
            ? value <= 3 ? 'text-green-400' : value >= 7 ? 'text-red-400' : 'text-yellow-400'
            : value >= 7 ? 'text-green-400' : value <= 3 ? 'text-red-400' : 'text-yellow-400'
        }`}>
          {value}/10 - {RATING_LABELS[value]}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={1}
        max={10}
        step={1}
        className="py-2"
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="checkins-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            DAILY <span className="text-primary">CHECK-INS</span>
          </h1>
          <p className="text-gray-400 mt-1">Track your progress and stay accountable</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowScheduleDialog(true)}
          className="border-border hover:border-primary"
        >
          <Settings className="w-4 h-4 mr-2" />
          Schedule
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-tactical text-center">
            <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{stats.total_completed}</div>
            <div className="text-xs text-gray-400">Completed (30d)</div>
          </div>
          <div className="card-tactical text-center">
            <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{stats.completion_rate}%</div>
            <div className="text-xs text-gray-400">Completion Rate</div>
          </div>
          <div className="card-tactical text-center">
            <Battery className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{stats.avg_energy || '-'}</div>
            <div className="text-xs text-gray-400">Avg Energy</div>
          </div>
          <div className="card-tactical text-center">
            <Scale className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <div className={`font-mono text-2xl ${
              stats.weight_trend > 0 ? 'text-red-400' : stats.weight_trend < 0 ? 'text-green-400' : 'text-white'
            }`}>
              {stats.weight_trend !== null ? `${stats.weight_trend > 0 ? '+' : ''}${stats.weight_trend}kg` : '-'}
            </div>
            <div className="text-xs text-gray-400">Weight Change</div>
          </div>
        </div>
      )}

      {/* Today's Check-in */}
      {!schedule?.frequency ? (
        <div className="card-tactical border-yellow-500/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-yellow-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg text-white font-semibold">Set Up Your Check-in Schedule</h3>
              <p className="text-gray-400 text-sm">Start tracking your daily progress to stay accountable</p>
            </div>
            <Button onClick={() => setShowScheduleDialog(true)} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              Set Up Now
            </Button>
          </div>
        </div>
      ) : todaysCheckin && todaysCheckin.status === 'scheduled' ? (
        <div className="card-tactical border-primary">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg text-white font-semibold">Today's Check-in Ready</h3>
              <p className="text-gray-400 text-sm">Take a moment to log your progress</p>
            </div>
            <Button 
              onClick={() => setActiveCheckin(todaysCheckin)}
              className="bg-primary hover:bg-red-700"
            >
              Complete Now
            </Button>
          </div>
        </div>
      ) : todaysCheckin && todaysCheckin.status === 'completed' ? (
        <div className="card-tactical border-green-500/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg text-white font-semibold">Today's Check-in Complete!</h3>
              <p className="text-gray-400 text-sm">Great job staying consistent</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Pending Check-ins */}
      {pendingCheckins.length > 0 && (
        <div className="card-tactical">
          <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-500" />
            Pending Check-ins ({pendingCheckins.length})
          </h3>
          <div className="space-y-2">
            {pendingCheckins.map((checkin) => (
              <div 
                key={checkin.id}
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-white">
                    {new Date(checkin.scheduled_date).toLocaleDateString('en-US', { 
                      weekday: 'short', month: 'short', day: 'numeric' 
                    })}
                  </span>
                  {checkin.status === 'missed' && (
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Missed</span>
                  )}
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setActiveCheckin(checkin)}
                  className="border-border hover:border-primary"
                >
                  Complete
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Check-ins */}
      <div className="card-tactical">
        <h3 className="font-heading text-lg text-white uppercase mb-4">Recent Check-ins</h3>
        {checkins.filter(c => c.status === 'completed').length > 0 ? (
          <div className="space-y-3">
            {checkins.filter(c => c.status === 'completed').slice(0, 7).map((checkin) => (
              <div 
                key={checkin.id}
                className="p-4 bg-secondary/30 rounded-sm border border-border"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-medium">
                    {new Date(checkin.scheduled_date).toLocaleDateString('en-US', { 
                      weekday: 'long', month: 'short', day: 'numeric' 
                    })}
                  </span>
                  <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded">
                    Completed
                  </span>
                </div>
                
                <div className="grid grid-cols-5 gap-2 text-center text-sm">
                  {checkin.energy_level && (
                    <div>
                      <Battery className="w-4 h-4 mx-auto text-yellow-500 mb-1" />
                      <div className="text-white font-mono">{checkin.energy_level}</div>
                      <div className="text-xs text-gray-500">Energy</div>
                    </div>
                  )}
                  {checkin.mood && (
                    <div>
                      <Smile className="w-4 h-4 mx-auto text-green-500 mb-1" />
                      <div className="text-white font-mono">{checkin.mood}</div>
                      <div className="text-xs text-gray-500">Mood</div>
                    </div>
                  )}
                  {checkin.sleep_quality && (
                    <div>
                      <Moon className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                      <div className="text-white font-mono">{checkin.sleep_quality}</div>
                      <div className="text-xs text-gray-500">Sleep</div>
                    </div>
                  )}
                  {checkin.soreness_level && (
                    <div>
                      <Flame className="w-4 h-4 mx-auto text-orange-500 mb-1" />
                      <div className="text-white font-mono">{checkin.soreness_level}</div>
                      <div className="text-xs text-gray-500">Soreness</div>
                    </div>
                  )}
                  {checkin.weight_kg && (
                    <div>
                      <Scale className="w-4 h-4 mx-auto text-purple-500 mb-1" />
                      <div className="text-white font-mono">{checkin.weight_kg}kg</div>
                      <div className="text-xs text-gray-500">Weight</div>
                    </div>
                  )}
                </div>
                
                {checkin.trainer_feedback && (
                  <div className="mt-3 p-2 bg-primary/10 rounded border border-primary/30">
                    <div className="text-xs text-primary uppercase mb-1 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      Trainer Feedback
                    </div>
                    <p className="text-sm text-gray-300">{checkin.trainer_feedback}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No completed check-ins yet</p>
            <p className="text-sm">Complete your first check-in to start tracking progress</p>
          </div>
        )}
      </div>

      {/* Complete Check-in Dialog */}
      <Dialog open={!!activeCheckin} onOpenChange={(open) => !open && setActiveCheckin(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading uppercase">
              Complete <span className="text-primary">Check-in</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Physical Metrics */}
            <div className="space-y-4">
              <h4 className="text-sm text-gray-400 uppercase tracking-wider">Physical Metrics</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.weight_kg}
                    onChange={(e) => setFormData({...formData, weight_kg: e.target.value})}
                    placeholder="75.5"
                    className="bg-secondary border-transparent mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Body Fat %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.body_fat_percentage}
                    onChange={(e) => setFormData({...formData, body_fat_percentage: e.target.value})}
                    placeholder="15.0"
                    className="bg-secondary border-transparent mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Self Assessment */}
            <div className="space-y-4">
              <h4 className="text-sm text-gray-400 uppercase tracking-wider">Self Assessment</h4>
              
              <RatingSlider 
                label="Energy Level" 
                icon={Battery}
                value={formData.energy_level}
                onChange={(v) => setFormData({...formData, energy_level: v})}
              />
              
              <RatingSlider 
                label="Mood" 
                icon={Smile}
                value={formData.mood}
                onChange={(v) => setFormData({...formData, mood: v})}
              />
              
              <RatingSlider 
                label="Sleep Quality" 
                icon={Moon}
                value={formData.sleep_quality}
                onChange={(v) => setFormData({...formData, sleep_quality: v})}
              />
              
              <RatingSlider 
                label="Muscle Soreness" 
                icon={Flame}
                value={formData.soreness_level}
                onChange={(v) => setFormData({...formData, soreness_level: v})}
                inverted
              />
              
              <RatingSlider 
                label="Stress Level" 
                icon={Brain}
                value={formData.stress_level}
                onChange={(v) => setFormData({...formData, stress_level: v})}
                inverted
              />
            </div>
            
            {/* Reflection */}
            <div className="space-y-4">
              <h4 className="text-sm text-gray-400 uppercase tracking-wider">Reflection</h4>
              
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Goals Progress
                </Label>
                <Textarea
                  value={formData.goals_progress}
                  onChange={(e) => setFormData({...formData, goals_progress: e.target.value})}
                  placeholder="How are you progressing towards your goals?"
                  className="bg-secondary border-transparent mt-1"
                  rows={2}
                />
              </div>
              
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Wins This Week
                </Label>
                <Textarea
                  value={formData.wins}
                  onChange={(e) => setFormData({...formData, wins: e.target.value})}
                  placeholder="What are you proud of?"
                  className="bg-secondary border-transparent mt-1"
                  rows={2}
                />
              </div>
              
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Challenges
                </Label>
                <Textarea
                  value={formData.challenges}
                  onChange={(e) => setFormData({...formData, challenges: e.target.value})}
                  placeholder="What obstacles are you facing?"
                  className="bg-secondary border-transparent mt-1"
                  rows={2}
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Additional Notes</Label>
                <Textarea
                  value={formData.client_notes}
                  onChange={(e) => setFormData({...formData, client_notes: e.target.value})}
                  placeholder="Anything else you want to share with your trainer?"
                  className="bg-secondary border-transparent mt-1"
                  rows={2}
                />
              </div>
            </div>
            
            <Button 
              onClick={handleCompleteCheckin}
              className="w-full bg-primary hover:bg-red-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Submit Check-in
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-white">Check-in Schedule</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Frequency</Label>
              <Select
                value={scheduleForm.frequency}
                onValueChange={(v) => setScheduleForm({...scheduleForm, frequency: v})}
              >
                <SelectTrigger className="bg-secondary border-transparent mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-gray-300">Preferred Time</Label>
              <Input
                type="time"
                value={scheduleForm.preferred_time}
                onChange={(e) => setScheduleForm({...scheduleForm, preferred_time: e.target.value})}
                className="bg-secondary border-transparent mt-1"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="reminder"
                checked={scheduleForm.reminder_enabled}
                onChange={(e) => setScheduleForm({...scheduleForm, reminder_enabled: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <Label htmlFor="reminder" className="text-gray-300 cursor-pointer">
                Enable reminder notifications
              </Label>
            </div>
            
            <Button onClick={handleSaveSchedule} className="w-full bg-primary hover:bg-red-700">
              Save Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
